import React, { useState, useEffect, useCallback } from 'react';
import { useSeasonYear, useSeasonYearContext } from '../context/SeasonYearContext';
import {
  Box,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Alert,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { API_URL } from '../config';
import { fetchApi, authedFetch } from '../utils/apiUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublishIcon from '@mui/icons-material/Publish';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupIcon from '@mui/icons-material/Group';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PeopleIcon from '@mui/icons-material/People';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RaceDetailsList from './RaceDetailsList';

// Type definitions
interface Division {
  division_id: number;
  division_name: string;
  division_code?: string;
  division_description?: string;
  is_active?: boolean;
}

interface Region {
  region_id: number;
  region_name: string;
  region_code?: string;
  region_description?: string;
  is_active?: boolean;
}

interface DivisionRegion {
  division_region_id: number;
  division_id: number;
  region_id: number;
  season_year: number;
  is_active: boolean;
  division_name?: string;
  region_name?: string;
}

interface Standing {
  driver_id: number;
  first_name: string;
  last_name: string;
  car_number?: string;
  hometown?: string;
  state?: string;
  total_points: number;
  races_competed: number;
  races_counted: number;
  feature_wins?: number;
  wins?: number;
  top5s?: number;
  top10s?: number;
  average_finish?: number;
  best_finish?: number;
  worst_finish?: number;
  position?: number;
  is_member?: number;
}

interface DriverRace {
  race_id: number;
  event_id: number;
  event_name?: string;
  event_date: string;
  track_name: string;
  track_city?: string;
  track_state?: string;
  race_type: string;
  finish_position: number | null;
  points_earned: number | null;
  car_count?: number;
  status?: 'counting' | 'replaced' | 'not_counted';
  status_reason?: string;
  points_counted?: boolean;
  is_weekly_best?: boolean;
  is_replaced?: boolean;
  week_number?: number;
  week_key?: string;
  show_week_header?: boolean;
  rule_type?: 'highest_only' | 'all_count' | null;
  race_limit_info?: {
    min_races: number;
    total_eligible: number;
    this_race_rank: number;
  } | null;
}

interface AlertState {
  show: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface StandingsResponse {
  drivers: Standing[];
  total: number;
  activeMembers: number;
  inactiveMembers: number;
}

const Standings: React.FC = () => {
  const [standings, setStandings] = useState<Standing[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [availableRegions, setAvailableRegions] = useState<Region[]>([]);
  const [selectedDivision, setSelectedDivision] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  
  const [alert, setAlert] = useState<AlertState>({ show: false, message: '', severity: 'success' });
  const [publishing, setPublishing] = useState<boolean>(false);
  
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedDriverName, setSelectedDriverName] = useState<string>('');
  const [selectedDriverRaces, setSelectedDriverRaces] = useState<DriverRace[]>([]);
  const [loadingRaces, setLoadingRaces] = useState<boolean>(false);
  
  const seasonYear = useSeasonYear();
  const { setSeasonYear: setGlobalSeasonYear } = useSeasonYearContext();
  const [seasonAnchorEl, setSeasonAnchorEl] = useState<null | HTMLElement>(null);
  const [yearOptions, setYearOptions] = useState<number[]>([seasonYear]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentStandingsType, setCurrentStandingsType] = useState<'preview' | 'published'>('preview');

  const [totalDriversInView, setTotalDriversInView] = useState<number>(0);
  const [activeMembersCount, setActiveMembersCount] = useState<number>(0);
  const [inactiveMembersCount, setInactiveMembersCount] = useState<number>(0);
  const [loadedKey, setLoadedKey] = useState<string>('');

  const currentKey = `${seasonYear}|${selectedDivision}|${selectedRegion}`;
  const standingsMatchCurrent = loadedKey === currentKey;

  useEffect(() => {
    authedFetch(`${API_URL}/config/available-seasons`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data: { years: number[] }) => {
        if (data.years?.length > 0) setYearOptions(data.years);
      })
      .catch(() => {});
  }, []);

  const columns: GridColDef[] = [
    { 
      field: 'position', 
      headerName: 'Pos', 
      width: 70,
      valueGetter: (params: GridValueGetterParams) => {
        const rowIndex = params.api?.getRowIndexRelativeToVisibleRows(params.row.driver_id);
        return rowIndex !== undefined ? rowIndex + 1 : '-';
      },
    },
    {
      field: 'driver_name',
      headerName: 'Driver',
      flex: 1,
      minWidth: 220,
      valueGetter: (params: GridValueGetterParams) => `${params.row.first_name} ${params.row.last_name}`,
      renderCell: (params: GridRenderCellParams<Standing>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {params.row.first_name} {params.row.last_name}
          </Typography>
          <Chip
            label={params.row.is_member ? 'Member' : 'Non-Member'}
            size="small"
            color={params.row.is_member ? 'success' : 'default'}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.675rem' }}
          />
        </Box>
      ),
    },
    { field: 'car_number', headerName: 'Car #', width: 80 },
    {
      field: 'total_points',
      headerName: 'Points',
      width: 100,
      type: 'number',
    },
    {
      field: 'races_counted', 
      headerName: 'Races',
      width: 80,
      type: 'number',
    },
    {
      field: 'feature_wins', 
      headerName: 'Wins',
      width: 80,
      type: 'number',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<Standing>) => (
        <Button
          size="small"
          variant="text"
          onClick={(e) => handleDriverClick(e, params.row.driver_id, `${params.row.first_name} ${params.row.last_name}`)}
        >
          View Races
        </Button>
      ),
    },
  ];

  // Use centralized fetchApi with global token expiration handling
  const fetchApiWithError = useCallback(async <T,>(url: string, options: RequestInit = {}, errorMessage: string): Promise<T> => {
    try {
      return await fetchApi(url, options) as T;
    } catch (error) {
      console.error(errorMessage, error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      setAlert({ show: true, message: `${errorMessage}: ${message}`, severity: 'error' });
      throw error;
    }
  }, []);

  const fetchDivisions = useCallback(async () => {
    try {
      const data = await fetchApiWithError<Division[]>(`${API_URL}/divisions`, {}, 'Error loading divisions');
      setDivisions(Array.isArray(data) ? data : []);
    } catch {
      setDivisions([]);
    }
  }, [fetchApiWithError]);

  const fetchRegions = useCallback(async () => {
    try {
      const data = await fetchApiWithError<Region[]>(`${API_URL}/regions`, {}, 'Error loading regions');
      setRegions(Array.isArray(data) ? data : []);
    } catch {
      setRegions([]);
    }
  }, [fetchApiWithError]);

  const fetchDivisionRegions = useCallback(async (divisionId: string) => {
    if (!divisionId) {
      setAvailableRegions([]);
      return;
    }
    try {
      const data = await fetchApiWithError<DivisionRegion[]>(`${API_URL}/division-regions/${divisionId}`, {}, 'Error loading regions for division');
      const activeDivisionRegions = regions.filter(region => 
        data.some(dr => dr.region_id === region.region_id)
      );
      setAvailableRegions(activeDivisionRegions);
    } catch {
      setAvailableRegions([]);
    }
  }, [fetchApiWithError, regions]);

  const fetchMemberStats = useCallback(async () => {
    if (!selectedDivision || !seasonYear) return;
    try {
      const queryParams = new URLSearchParams({
        division_id: selectedDivision,
        season_year: seasonYear.toString(),
      });
      if (selectedRegion) {
        queryParams.append('region_id', selectedRegion);
      }

      const data = await fetchApiWithError<StandingsResponse>(`${API_URL}/standings?${queryParams.toString()}`, {}, 'Error loading member stats');
      if (data) {
        setActiveMembersCount(data.activeMembers || 0);
        setInactiveMembersCount(data.inactiveMembers || 0);
      }
    } catch {
      setActiveMembersCount(0);
      setInactiveMembersCount(0);
    }
  }, [fetchApiWithError, selectedDivision, seasonYear, selectedRegion]);

  const fetchPreviewStandings = useCallback(async () => {
    const key = `${seasonYear}|${selectedDivision}|${selectedRegion}`;
    if (!selectedDivision) {
      setStandings([]);
      setTotalDriversInView(0);
      setLoadedKey(key);
      return;
    }

    setLoading(true);
    setCurrentStandingsType('preview');
    try {
      const payload: { division_id: string; season_year: number; region_id?: string } = {
        division_id: selectedDivision,
        season_year: seasonYear,
      };
      if (selectedRegion) {
        payload.region_id = selectedRegion;
      }

      const previewData = await fetchApiWithError<Standing[]>(`${API_URL}/standings/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }, 'Error loading preview standings');

      setStandings(Array.isArray(previewData) ? previewData : []);
      setTotalDriversInView(Array.isArray(previewData) ? previewData.length : 0);
      setLoadedKey(key);
      fetchMemberStats();

    } catch {
      setStandings([]);
      setTotalDriversInView(0);
    } finally {
      setLoading(false);
    }
  }, [selectedDivision, selectedRegion, seasonYear, fetchApiWithError, fetchMemberStats]);

  const handlePreviewPoints = async () => {
    await fetchPreviewStandings();
  };

  const handlePublishPoints = async () => {
    if (!selectedDivision) {
      setAlert({ show: true, message: 'Please select a division', severity: 'warning' });
      return;
    }
    if (currentStandingsType !== 'preview') {
      setAlert({ show: true, message: 'No preview data to publish. Please preview points first.', severity: 'warning' });
      return;
    }
    if (!standingsMatchCurrent) {
      setAlert({ show: true, message: 'Preview is out of date with current filters. Refresh before publishing.', severity: 'warning' });
      return;
    }

    setPublishing(true);
    try {
      const publishBody: { division_id: string; season_year: number; region_id?: string } = {
        division_id: selectedDivision,
        season_year: seasonYear,
      };
      if (selectedRegion) publishBody.region_id = selectedRegion;

      await fetchApi(`${API_URL}/standings/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(publishBody),
      });

      setAlert({ show: true, message: 'Points published successfully!', severity: 'success' });
      await fetchPreviewStandings();
    } catch {
      // Error already handled by fetchApi
    } finally {
      setPublishing(false);
    }
  };

  const handleDriverClick = useCallback(async (event: React.MouseEvent | null, driverId: number, driverName: string) => {
    if (event) event.preventDefault();
    if (!selectedDivision) {
      setAlert({ show: true, message: "Division not selected", severity: 'warning' });
      return;
    }

    setSelectedDriverName(driverName);
    setDialogOpen(true);
    setLoadingRaces(true);
    setSelectedDriverRaces([]);
    
    try {
      const queryParams = new URLSearchParams({
        driver_id: driverId.toString(),
        division_id: selectedDivision,
        season_year: seasonYear.toString(),
      });
      if (selectedRegion) {
        queryParams.append('region_id', selectedRegion);
      }
      const data = await fetchApi(`${API_URL}/standings/driver-races?${queryParams.toString()}`, {}) as DriverRace[];
      setSelectedDriverRaces(Array.isArray(data) ? data : []);
    } catch {
      setSelectedDriverRaces([]);
    } finally {
      setLoadingRaces(false);
    }
  }, [selectedDivision, selectedRegion, seasonYear]);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedDriverRaces([]);
    setSelectedDriverName('');
  };

  const handleExportStandings = async () => {
    if (!selectedDivision) {
      setAlert({ show: true, message: 'Please select a division first', severity: 'warning' });
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        division_id: selectedDivision,
        season_year: seasonYear.toString(),
      });
      
      if (selectedRegion) {
        queryParams.append('region_id', selectedRegion);
      }

      // Use fetch directly (not fetchApi) since we're handling a file download, not JSON
      const response = await authedFetch(`${API_URL}/standings/export?${queryParams.toString()}`);
      
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = 'Failed to export standings';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Get the filename from the response headers or create a default one
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'standings.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setAlert({ show: true, message: 'Standings exported successfully!', severity: 'success' });

    } catch (error) {
      console.error('Export error:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      setAlert({ show: true, message: `Failed to export standings: ${message}`, severity: 'error' });
    }
  };

  useEffect(() => {
    fetchDivisions();
    fetchRegions();
  }, [fetchDivisions, fetchRegions]);

  useEffect(() => {
    if (selectedDivision) {
      fetchDivisionRegions(selectedDivision);
    } else {
      setAvailableRegions([]);
      setSelectedRegion('');
    }
  }, [selectedDivision, fetchDivisionRegions]);
  
  useEffect(() => {
    fetchPreviewStandings();
  }, [fetchPreviewStandings]);

  const handleDivisionChange = (e: SelectChangeEvent<string>) => {
    setSelectedDivision(e.target.value);
    setSelectedRegion('');
  };

  const handleRegionChange = (e: SelectChangeEvent<string>) => {
    setSelectedRegion(e.target.value);
  };

  const stats = [
    {
      label: 'Leading Points',
      value: standings.length > 0 && standings[0] ? standings[0].total_points : '-',
      icon: <EmojiEventsIcon />,
    },
    {
      label: 'Drivers in Preview',
      value: totalDriversInView,
      icon: <GroupIcon />,
    },
    {
      label: 'Active Members',
      value: activeMembersCount > 0 ? activeMembersCount : '-',
      icon: <PeopleIcon />,
    },
    {
      label: 'Inactive Members',
      value: inactiveMembersCount > 0 ? inactiveMembersCount : '-',
      icon: <PeopleOutlineIcon />,
    },
  ];

  const divisionLabel = divisions.find((d) => d.division_id.toString() === selectedDivision)?.division_name;
  const regionLabel = availableRegions.find((r) => r.region_id.toString() === selectedRegion)?.region_name;
  const contextLabel = selectedDivision
    ? `${divisionLabel || 'Division'} · ${selectedRegion ? `${regionLabel} Regional` : 'National'} Points Preview`
    : 'Select a division to view standings';

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Standings
          </Typography>
          <Button
            onClick={(e) => setSeasonAnchorEl(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 9999,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '0.8125rem',
              py: 0.25,
              px: 1.5,
              minHeight: 28,
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.disabled' },
            }}
          >
            <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.75, fontWeight: 500 }}>
              Season
            </Typography>
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {seasonYear}
            </Typography>
          </Button>
          <Menu
            anchorEl={seasonAnchorEl}
            open={Boolean(seasonAnchorEl)}
            onClose={() => setSeasonAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: 0.5, minWidth: 100 } } }}
          >
            {yearOptions.map((y) => (
              <MenuItem
                key={y}
                selected={y === seasonYear}
                onClick={() => {
                  setGlobalSeasonYear(y);
                  setSeasonAnchorEl(null);
                }}
              >
                {y}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={handlePreviewPoints}
            disabled={loading || !selectedDivision}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleExportStandings}
            disabled={loading || !selectedDivision}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={handlePublishPoints}
            disabled={publishing || loading || !selectedDivision || standings.length === 0 || !standingsMatchCurrent}
          >
            Publish
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid key={stat.label} item xs={6} sm={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.04em', fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {stat.label}
                  </Typography>
                  <Box sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center', '& svg': { fontSize: 18 } }}>
                    {stat.icon}
                  </Box>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Division</InputLabel>
            <Select value={selectedDivision} onChange={handleDivisionChange} label="Division">
              {divisions.map((division) => (
                <MenuItem key={division.division_id} value={division.division_id.toString()}>
                  {division.division_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Region</InputLabel>
            <Select
              value={selectedRegion}
              onChange={handleRegionChange}
              label="Region"
              disabled={!selectedDivision || availableRegions.length === 0}
            >
              <MenuItem value="">
                <em>National Points</em>
              </MenuItem>
              {availableRegions.map((region) => (
                <MenuItem key={region.region_id} value={region.region_id.toString()}>
                  {region.region_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedRegion && (
            <Button
              variant="text"
              size="small"
              onClick={() => handleRegionChange({ target: { value: '' } } as SelectChangeEvent<string>)}
            >
              View National Points
            </Button>
          )}
        </Box>
      </Paper>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.04em', fontWeight: 500 }}>
          {contextLabel}
        </Typography>
      </Box>

      <Paper sx={{ height: 700, width: '100%' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : !selectedDivision ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Please select a division to view standings preview.
            </Typography>
          </Box>
        ) : standings.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No standings data available for the selected criteria to preview.
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={standings}
            columns={columns}
            getRowId={(row) => row.driver_id}
            loading={loading || publishing}
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
                csvOptions: { allColumns: true, fileName: `standings-preview-${selectedDivision || 'all'}${selectedRegion ? '-' + selectedRegion : ''}` },
                printOptions: { disableToolbarButton: true },
              },
            }}
            disableSelectionOnClick
            density="compact"
            sx={{
              border: 'none',
              fontFamily: 'inherit',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'transparent',
                borderBottom: '1px solid',
                borderColor: 'divider',
                minHeight: '40px !important',
                maxHeight: '40px !important',
                lineHeight: '40px !important',
              },
              '& .MuiDataGrid-columnHeader': {
                padding: '0 12px',
                '&:focus, &:focus-within': { outline: 'none' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                },
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: '0.75rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'text.secondary',
              },
              '& .MuiDataGrid-columnSeparator': { display: 'none' },
              '& .MuiDataGrid-cell': {
                padding: '0 12px',
                fontSize: '0.875rem',
                color: 'text.primary',
                borderBottom: '1px solid',
                borderColor: 'grey.100',
                '&:focus, &:focus-within': { outline: 'none' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                },
              },
              '& .MuiDataGrid-row': {
                transition: 'background-color 120ms',
                '&:hover': { backgroundColor: 'action.hover' },
              },
              '& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': {
                borderBottom: 'none',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid',
                borderColor: 'divider',
                minHeight: '44px',
              },
              '& .MuiTablePagination-root': {
                fontSize: '0.8125rem',
              },
            }}
          />
        )}
      </Paper>

      {alert.show && (
        <Alert 
          severity={alert.severity}
          sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1300 }}
          onClose={() => setAlert({ ...alert, show: false })}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setAlert({ ...alert, show: false });
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {alert.message}
        </Alert>
      )}

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
          {selectedDriverName}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {divisionLabel}
            {selectedRegion ? ` · ${regionLabel} Regional` : ' · National Points'}
          </Typography>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingRaces ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedDriverRaces.length > 0 ? (
            <RaceDetailsList 
              races={selectedDriverRaces}
              totalPoints={selectedDriverRaces.reduce((sum, race) => sum + (race.points_counted ? (race.points_earned || 0) : 0), 0)}
            />
          ) : (
            <Typography sx={{ p: 2, textAlign: 'center' }}>No race details found for this driver in the selected context.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Standings;
